import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import { LayoutDashboard, KanbanSquare, Plus, LogOut, Trash2, CalendarRange, Ticket, Users, Link2, Menu, CalendarOff, FileSearch, Gauge } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import Logo from "@/components/Logo";
import InstallPrompt from "@/components/InstallPrompt";
import NotificationBell from "@/components/NotificationBell";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { initials } from "@/lib/ui";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { boardId } = useParams();
  const location = useLocation();
  const [boards, setBoards] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia("(min-width: 768px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const fn = (e) => { setIsDesktop(e.matches); if (e.matches) setMenuOpen(false); };
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const loadBoards = async () => {
    if (user?.role === "client") { setBoards([]); return; }
    const { data } = await api.get("/boards");
    setBoards(data);
  };
  useEffect(() => { loadBoards(); }, [user]);

  const createBoard = async () => {
    if (!name.trim()) return;
    try {
      const { data } = await api.post("/boards", { name, description: desc });
      toast.success("Board created");
      setOpen(false); setName(""); setDesc("");
      await loadBoards();
      navigate(`/boards/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create board");
    }
  };

  const deleteBoard = async (id, e) => {
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm("Delete this board and all its tasks?")) return;
    await api.delete(`/boards/${id}`);
    toast.success("Board deleted");
    await loadBoards();
    if (boardId === id) navigate("/dashboard");
  };

  const isManager = user?.role !== "engineer" && user?.role !== "client";
  const isClient = user?.role === "client";
  const navCls = (active) => `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
    active ? "bg-indigo-50 text-indigo-700" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`;

  const tabs = [
    { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", id: "dashboard" },
    { to: "/tickets", icon: Ticket, label: "Tickets", id: "tickets" },
    isClient
      ? { to: "/portals", icon: Link2, label: "Portals", id: "portals" }
      : { to: "/sprints", icon: CalendarRange, label: "Sprints", id: "sprints" },
  ];
  const tabCls = (active) => `flex-1 flex flex-col items-center justify-center gap-0.5 text-[11px] font-medium transition-colors ${
    active ? "text-indigo-600" : "text-slate-500"}`;

  const sidebar = (
    <>
        <div className="h-16 shrink-0 flex items-center justify-between px-5 border-b border-slate-100">
          <Logo />
          {isDesktop && <NotificationBell id="sidebar-bell" />}
        </div>
        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          <Link to="/dashboard" data-testid="nav-dashboard" className={navCls(location.pathname === "/dashboard")}>
            <LayoutDashboard className="w-4 h-4" /> Dashboard
          </Link>
          {!isClient && (
            <Link to="/sprints" data-testid="nav-sprints" className={navCls(location.pathname.startsWith("/sprints"))}>
              <CalendarRange className="w-4 h-4" /> Sprints
            </Link>
          )}
          <Link to="/tickets" data-testid="nav-tickets" className={navCls(location.pathname.startsWith("/tickets"))}>
            <Ticket className="w-4 h-4" /> Tickets
          </Link>
          <Link to="/portals" data-testid="nav-portals" className={navCls(location.pathname.startsWith("/portals"))}>
            <Link2 className="w-4 h-4" /> Client Portals
          </Link>
          <Link to="/accuracy" data-testid="nav-accuracy" className={navCls(location.pathname.startsWith("/accuracy"))}>
            <Gauge className="w-4 h-4" /> Accuracy Tracker
          </Link>
          {!isClient && (
            <Link to="/rca" data-testid="nav-rca" className={navCls(location.pathname.startsWith("/rca"))}>
              <FileSearch className="w-4 h-4" /> RCA
            </Link>
          )}
          {!isClient && (
            <Link to="/hr" data-testid="nav-hr" className={navCls(location.pathname.startsWith("/hr"))}>
              <CalendarOff className="w-4 h-4" /> HR
            </Link>
          )}
          {isManager && (
            <Link to="/team" data-testid="nav-team" className={navCls(location.pathname.startsWith("/team"))}>
              <Users className="w-4 h-4" /> Team &amp; Clients
            </Link>
          )}
          {!isClient && (
          <>
          <div className="pt-4 pb-1.5 px-3 flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Boards</span>
            {isManager && (
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <button className="text-slate-400 hover:text-indigo-600 transition-colors" data-testid="create-board-button">
                    <Plus className="w-4 h-4" />
                  </button>
                </DialogTrigger>
                <DialogContent data-testid="create-board-dialog">
                  <DialogHeader><DialogTitle>Create board</DialogTitle></DialogHeader>
                  <div className="space-y-4 py-2">
                    <div className="space-y-2">
                      <Label>Name</Label>
                      <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Q3 Pilot Launch" data-testid="board-name-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="What is this board for?" />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={createBoard} className="bg-indigo-600 hover:bg-indigo-700" data-testid="board-create-submit">Create board</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
          {boards.map((b) => (
            <Link key={b.id} to={`/boards/${b.id}`} data-testid={`nav-board-${b.id}`}
              className={`group flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                boardId === b.id ? "bg-indigo-50 text-indigo-700" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`}>
              <KanbanSquare className="w-4 h-4 shrink-0" />
              <span className="truncate flex-1">{b.name}</span>
              <span className="text-xs text-slate-400">{b.task_count}</span>
              {isManager && (
                <button onClick={(e) => deleteBoard(b.id, e)}
                  className="opacity-100 md:opacity-0 md:group-hover:opacity-100 text-slate-300 hover:text-red-500 transition-opacity"
                  data-testid={`delete-board-${b.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </Link>
          ))}
          {boards.length === 0 && <p className="px-3 text-xs text-slate-400">No boards yet.</p>}
          </>
          )}
        </nav>
        <div className="p-3 border-t border-slate-100">
          <div className="flex items-center gap-2.5 px-2 py-2">
            <Avatar className="w-9 h-9">
              <AvatarImage src={user?.avatar} />
              <AvatarFallback className="bg-indigo-100 text-indigo-700 text-xs font-semibold">{initials(user?.name)}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <Badge variant="outline" className="h-4 px-1.5 text-[10px] font-semibold uppercase tracking-wide border-slate-200 text-slate-500"
                data-testid="nav-role-badge">{user?.role === "engineer" ? "employee" : user?.role}</Badge>
            </div>
            <button onClick={logout} className="text-slate-400 hover:text-red-500 transition-colors" data-testid="nav-logout-button">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-slate-50">
      {isDesktop && (
        <aside className="w-64 shrink-0 bg-white border-r border-slate-200 flex-col hidden md:flex fixed inset-y-0 left-0 z-30">
          {sidebar}
        </aside>
      )}

      <div className="md:hidden fixed top-0 inset-x-0 h-14 bg-white/90 backdrop-blur-md border-b border-slate-200 z-30 flex items-center gap-3 px-3">
        <button onClick={() => setMenuOpen(true)} className="w-10 h-10 -ml-1 rounded-lg flex items-center justify-center text-slate-600 active:bg-slate-100" data-testid="mobile-menu-button" aria-label="Open menu">
          <Menu className="w-5 h-5" />
        </button>
        <Link to="/dashboard" className="flex-1 min-w-0"><Logo /></Link>
        <NotificationBell id="mobile-bell" />
        <button onClick={logout} className="w-10 h-10 rounded-lg flex items-center justify-center active:bg-slate-100" data-testid="mobile-logout" aria-label="Log out"><LogOut className="w-5 h-5 text-slate-500" /></button>
      </div>

      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side="left" className="w-[84vw] max-w-[300px] p-0 flex flex-col" data-testid="mobile-sidebar">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          {!isDesktop && sidebar}
        </SheetContent>
      </Sheet>

      <main className="flex-1 md:ml-64 pt-14 pb-[calc(4rem+env(safe-area-inset-bottom))] md:pt-0 md:pb-0 min-w-0">{children}</main>

      <InstallPrompt />

      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur-md border-t border-slate-200 flex h-[calc(4rem+env(safe-area-inset-bottom))] pb-[env(safe-area-inset-bottom)]" data-testid="mobile-bottom-nav">
        {tabs.map(({ to, icon: Icon, label, id }) => (
          <Link key={id} to={to} className={tabCls(location.pathname.startsWith(to))} data-testid={`mobile-tab-${id}`}>
            <Icon className="w-5 h-5" />{label}
          </Link>
        ))}
        <button onClick={() => setMenuOpen(true)} className={tabCls(menuOpen)} data-testid="mobile-tab-menu">
          <Menu className="w-5 h-5" />More
        </button>
      </nav>
    </div>
  );
}
